import React, { useState, useEffect, useRef } from 'react';
import { Clock, Pause, Play, Square, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SaveTimerEntryModal from './SaveTimerEntryModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const RunningTimer = ({ onTimerStop }) => {
  const [timer, setTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [idleTime, setIdleTime] = useState(0);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [timerDataForSave, setTimerDataForSave] = useState(null);
  const intervalRef = useRef(null);
  const idleIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    loadActiveTimer();
    
    // Check for active timer every 30 seconds
    const checkInterval = setInterval(loadActiveTimer, 30000);
    
    return () => {
      clearInterval(checkInterval);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (idleIntervalRef.current) clearInterval(idleIntervalRef.current);
    };
  }, []);

  // Update elapsed time every second
  useEffect(() => {
    if (!timer || timer.status === 'paused') {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      const startTime = new Date(timer.started_at);
      const now = new Date();
      const seconds = Math.floor((now - startTime) / 1000) + (timer.accumulated_seconds || 0);
      setElapsed(seconds);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timer]);

  // Idle detection (10 minutes)
  useEffect(() => {
    if (!timer || timer.status === 'paused') {
      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
      }
      return;
    }

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      setIdleTime(0);
    };

    // Reset idle timer on user activity
    window.addEventListener('mousemove', resetIdleTimer);
    window.addEventListener('keypress', resetIdleTimer);
    window.addEventListener('click', resetIdleTimer);

    // Check idle time every second
    idleIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const idle = Math.floor((now - lastActivityRef.current) / 1000);
      setIdleTime(idle);

      // 10 minutes = 600 seconds
      if (idle >= 600) {
        handleIdleWarning();
      }
    }, 1000);

    return () => {
      window.removeEventListener('mousemove', resetIdleTimer);
      window.removeEventListener('keypress', resetIdleTimer);
      window.removeEventListener('click', resetIdleTimer);
      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
      }
    };
  }, [timer]);

  const handleIdleWarning = async () => {
    // Clear interval to prevent multiple prompts
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
    }

    const shouldPause = window.confirm(
      "You've been idle for 10 minutes. Would you like to pause the timer?"
    );

    if (shouldPause) {
      await handlePause();
    } else {
      // Reset idle tracking
      lastActivityRef.current = Date.now();
      setIdleTime(0);
    }
  };

  const loadActiveTimer = async () => {
    try {
      const response = await axios.get(`${API}/timer/active`);
      if (response.data.timer) {
        setTimer(response.data.timer);
        
        // Auto-minimize on mobile
        if (window.innerWidth < 768) {
          setIsMinimized(true);
        }
      } else {
        setTimer(null);
        setElapsed(0);
      }
    } catch (error) {
      console.error('Failed to load timer:', error);
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API}/timer/pause`);
      await loadActiveTimer();
      toast.info('Timer paused');
    } catch (error) {
      console.error('Failed to pause timer:', error);
      toast.error('Failed to pause timer');
    }
  };

  const handleResume = async () => {
    try {
      await axios.post(`${API}/timer/resume`);
      await loadActiveTimer();
      toast.success('Timer resumed');
    } catch (error) {
      console.error('Failed to resume timer:', error);
      toast.error('Failed to resume timer');
    }
  };

  const handleStop = () => {
    // Calculate total time
    let totalSeconds = timer.accumulated_seconds || 0;
    
    if (timer.status === 'running') {
      const startTime = new Date(timer.started_at);
      const now = new Date();
      const elapsedSecs = Math.floor((now - startTime) / 1000);
      totalSeconds += elapsedSecs;
    }
    
    const totalMinutes = Math.ceil(totalSeconds / 60);
    
    // Prepare entry data for modal
    const startTime = new Date(timer.created_at);
    const endTime = new Date(startTime.getTime() + totalSeconds * 1000);
    
    const entryData = {
      project_id: timer.project_id,
      project_name: timer.project_name,
      project_color: timer.project_color,
      duration_minutes: totalMinutes,
      start_time: startTime,
      end_time: endTime,
      description: timer.description || '',
      entry_type: timer.entry_type || 'work'
    };
    
    setTimerDataForSave(entryData);
    setSaveModalOpen(true);
  };

  const handleSaveComplete = () => {
    setTimer(null);
    setElapsed(0);
    setSaveModalOpen(false);
    setTimerDataForSave(null);
    if (onTimerStop) onTimerStop();
  };

  if (!timer) return null;

  // Format elapsed time as HH:MM:SS
  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;
  const formatted = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  // Minimized view (for mobile or user preference)
  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        className="fixed top-20 right-4 bg-red-600 text-white px-4 py-2 rounded-full shadow-lg cursor-pointer z-50 flex items-center gap-2 animate-pulse"
        data-testid="timer-minimized"
      >
        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
        <span className="font-mono font-bold">{formatted}</span>
      </div>
    );
  }

  // Full timer widget
  return (
    <div
      className="fixed top-20 right-4 bg-white rounded-xl shadow-2xl p-5 z-50 w-80 border-2 border-gray-200"
      data-testid="timer-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {timer.status === 'running' ? (
            <span className="flex items-center gap-2 text-red-600 font-semibold text-sm">
              <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
              Timer Running
            </span>
          ) : (
            <span className="flex items-center gap-2 text-yellow-600 font-semibold text-sm">
              <Pause className="w-4 h-4" />
              Timer Paused
            </span>
          )}
        </div>
        <button
          onClick={() => setIsMinimized(true)}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Minimize"
        >
          <Minimize2 className="w-4 h-4" />
        </button>
      </div>

      {/* Project */}
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: timer.project_color }}
          />
          <span className="font-semibold text-gray-800">{timer.project_name}</span>
        </div>
      </div>

      {/* Timer Display */}
      <div className="my-4 text-center">
        <div className="text-5xl font-bold font-mono text-gray-900 tracking-wider">
          {formatted}
        </div>
      </div>

      {/* Description */}
      {timer.description && (
        <div className="mb-4 text-sm text-gray-600 italic">
          {timer.description}
        </div>
      )}

      {/* Controls */}
      <div className="flex gap-2">
        {timer.status === 'running' ? (
          <button
            onClick={handlePause}
            className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition-all"
          >
            <Pause className="w-4 h-4" />
            Pause
          </button>
        ) : (
          <button
            onClick={handleResume}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-all"
          >
            <Play className="w-4 h-4" />
            Resume
          </button>
        )}
        <button
          onClick={handleStop}
          className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-all"
        >
          <Square className="w-4 h-4" />
          Stop
        </button>
      </div>

      {/* Save Timer Entry Modal */}
      <SaveTimerEntryModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        timerData={timerDataForSave}
        onSave={handleSaveComplete}
      />
    </div>
  );
};

const pad = (num) => num.toString().padStart(2, '0');

export default RunningTimer;
